import request from './utils';
import * as ROUTES from 'src/api/routes';
import type {
    CreateDocumentScanTypeInput,
    CreateDocumentScanTypeFromTemplateInput,
    DocumentScanTypeAdminSummary,
    DocumentScanTypeFacilityAvailabilitySummary,
    DocumentScanTypeFormDefinitionAssociation,
    DocumentScanTypeTemplate,
    FormDefinitionDocumentScanTypeAssociation,
    FacilityAvailabilityMode,
} from 'src/queries/useDocumentScanTypesAdmin';

export async function getDocumentScanTypeTemplates(): Promise<DocumentScanTypeTemplate[]> {
    const result = await request(ROUTES.ADMIN_GET_DOCUMENT_SCAN_TYPE_TEMPLATES);
    return result.data.templates || [];
}

export async function getAdminDocumentScanTypes(): Promise<DocumentScanTypeAdminSummary[]> {
    const result = await request(ROUTES.ADMIN_GET_DOCUMENT_SCAN_TYPES);
    return result.data.documentScanTypes || [];
}

export async function getAdminDocumentScanType(
    documentScanTypeId: string,
): Promise<DocumentScanTypeAdminSummary | null> {
    const result = await request({
        ...ROUTES.ADMIN_GET_DOCUMENT_SCAN_TYPE,
        pathParams: { documentScanTypeId },
    });
    return result.data.documentScanType || null;
}

export async function createDocumentScanTypeFromTemplate(
    data: CreateDocumentScanTypeFromTemplateInput,
): Promise<DocumentScanTypeAdminSummary> {
    const result = await request({ ...ROUTES.ADMIN_CREATE_DOCUMENT_SCAN_TYPE_FROM_TEMPLATE, data });
    return result.data.documentScanType;
}

export async function createDocumentScanType(
    data: CreateDocumentScanTypeInput,
): Promise<DocumentScanTypeAdminSummary> {
    const result = await request({ ...ROUTES.ADMIN_CREATE_DOCUMENT_SCAN_TYPE, data });
    return result.data.documentScanType;
}

export async function createDocumentScanTypeDraft(documentScanTypeId: string) {
    const result = await request({
        ...ROUTES.ADMIN_CREATE_DOCUMENT_SCAN_TYPE_DRAFT,
        pathParams: { documentScanTypeId },
    });
    return result.data.draftVersion;
}

export async function updateDocumentScanTypeDraft({
    documentScanTypeId,
    documentScanTypeVersionId,
    data,
}: {
    documentScanTypeId: string;
    documentScanTypeVersionId: string;
    data: Record<string, unknown>;
}) {
    const result = await request({
        ...ROUTES.ADMIN_UPDATE_DOCUMENT_SCAN_TYPE_DRAFT,
        pathParams: { documentScanTypeId, documentScanTypeVersionId },
        data,
    });
    return result.data.draftVersion;
}

export async function updateDocumentScanTypeSettings({
    documentScanTypeId,
    data,
}: {
    documentScanTypeId: string;
    data: { name?: string; description?: string | null };
}) {
    const result = await request({
        ...ROUTES.ADMIN_UPDATE_DOCUMENT_SCAN_TYPE_SETTINGS,
        pathParams: { documentScanTypeId },
        data,
    });
    return result.data.documentScanType;
}

export async function validateDocumentScanTypeDraft({
    documentScanTypeId,
    documentScanTypeVersionId,
}: {
    documentScanTypeId: string;
    documentScanTypeVersionId: string;
}) {
    const result = await request({
        ...ROUTES.ADMIN_VALIDATE_DOCUMENT_SCAN_TYPE_DRAFT,
        pathParams: { documentScanTypeId, documentScanTypeVersionId },
    });
    return result.data.validation;
}

export async function testDocumentScanTypeDraft({
    documentScanTypeId,
    documentScanTypeVersionId,
    imageBase64,
}: {
    documentScanTypeId: string;
    documentScanTypeVersionId: string;
    imageBase64?: string | null;
}) {
    const result = await request({
        ...ROUTES.ADMIN_TEST_DOCUMENT_SCAN_TYPE_DRAFT,
        pathParams: { documentScanTypeId, documentScanTypeVersionId },
        data: { imageBase64 },
    });
    return result.data;
}

export async function testDocumentScanTypeVersion({
    documentScanTypeId,
    documentScanTypeVersionId,
    facilityId,
    imageBase64,
}: {
    documentScanTypeId: string;
    documentScanTypeVersionId: string;
    facilityId?: number | null;
    imageBase64?: string | null;
}) {
    const result = await request({
        ...ROUTES.ADMIN_TEST_DOCUMENT_SCAN_TYPE_VERSION,
        pathParams: { documentScanTypeId, documentScanTypeVersionId },
        data: { facilityId, imageBase64 },
    });
    return result.data;
}

export async function publishDocumentScanTypeDraft({
    documentScanTypeId,
    documentScanTypeVersionId,
}: {
    documentScanTypeId: string;
    documentScanTypeVersionId: string;
}) {
    const result = await request({
        ...ROUTES.ADMIN_PUBLISH_DOCUMENT_SCAN_TYPE_DRAFT,
        pathParams: { documentScanTypeId, documentScanTypeVersionId },
    });
    return result.data;
}

export async function publishDocumentScanTypeVersion({
    documentScanTypeId,
    documentScanTypeVersionId,
}: {
    documentScanTypeId: string;
    documentScanTypeVersionId: string;
}) {
    const result = await request({
        ...ROUTES.ADMIN_PUBLISH_DOCUMENT_SCAN_TYPE_VERSION,
        pathParams: { documentScanTypeId, documentScanTypeVersionId },
    });
    return result.data;
}

export async function getDocumentScanTypeFacilityAvailability(
    documentScanTypeId: string,
): Promise<DocumentScanTypeFacilityAvailabilitySummary> {
    const result = await request({
        ...ROUTES.ADMIN_GET_DOCUMENT_SCAN_TYPE_FACILITY_AVAILABILITY,
        pathParams: { documentScanTypeId },
    });
    return result.data.availability;
}

export async function updateDocumentScanTypeFacilityAvailability({
    documentScanTypeId,
    mode,
    facilityIds,
}: {
    documentScanTypeId: string;
    mode: FacilityAvailabilityMode;
    facilityIds: number[];
}): Promise<DocumentScanTypeFacilityAvailabilitySummary> {
    const result = await request({
        ...ROUTES.ADMIN_UPDATE_DOCUMENT_SCAN_TYPE_FACILITY_AVAILABILITY,
        pathParams: { documentScanTypeId },
        data: { mode, facilityIds },
    });
    return result.data.facilityAvailability;
}

export async function getDocumentScanTypeFormDefinitions(
    documentScanTypeId: string,
): Promise<DocumentScanTypeFormDefinitionAssociation[]> {
    const result = await request({
        ...ROUTES.ADMIN_GET_DOCUMENT_SCAN_TYPE_FORM_DEFINITIONS,
        pathParams: { documentScanTypeId },
    });
    return result.data.formDefinitions || [];
}

export async function addDocumentScanTypeFormDefinition({
    documentScanTypeId,
    formDefinitionId,
}: {
    documentScanTypeId: string;
    formDefinitionId: number;
}): Promise<DocumentScanTypeFormDefinitionAssociation> {
    const result = await request({
        ...ROUTES.ADMIN_ADD_DOCUMENT_SCAN_TYPE_FORM_DEFINITION,
        pathParams: { documentScanTypeId },
        data: { formDefinitionId },
    });
    return result.data.formDefinition;
}

export async function deactivateDocumentScanTypeFormDefinition({
    documentScanTypeId,
    documentScanTypeFormDefinitionId,
}: {
    documentScanTypeId: string;
    documentScanTypeFormDefinitionId: string;
}): Promise<DocumentScanTypeFormDefinitionAssociation> {
    const result = await request({
        ...ROUTES.ADMIN_DEACTIVATE_DOCUMENT_SCAN_TYPE_FORM_DEFINITION,
        pathParams: { documentScanTypeId, documentScanTypeFormDefinitionId },
    });
    return result.data.formDefinition;
}

export async function getFormDefinitionDocumentScanTypes(
    formDefinitionId: number,
): Promise<FormDefinitionDocumentScanTypeAssociation[]> {
    const result = await request({
        ...ROUTES.ADMIN_GET_FORM_DEFINITION_DOCUMENT_SCAN_TYPES,
        pathParams: { formDefinitionId },
    });
    return result.data.documentScanTypes || [];
}

export async function createFacilityDocumentScanType({
    facilityId,
    data,
}: {
    facilityId: number;
    data: Record<string, unknown>;
}) {
    const result = await request({
        ...ROUTES.ADMIN_CREATE_FACILITY_DOCUMENT_SCAN_TYPE,
        pathParams: { facilityId },
        data,
    });
    return result.data;
}

export async function createDocumentScanTypeFacilityOverrideDraft({
    documentScanTypeId,
    facilityId,
}: {
    documentScanTypeId: string;
    facilityId: number;
}) {
    const result = await request({
        ...ROUTES.ADMIN_CREATE_DOCUMENT_SCAN_TYPE_FACILITY_OVERRIDE_DRAFT,
        pathParams: { documentScanTypeId, facilityId },
    });
    return result.data;
}

export async function updateDocumentScanTypeFacilityOverrideDraft({
    documentScanTypeId,
    facilityId,
    facilityDocumentScanTypeVersionId,
    data,
}: {
    documentScanTypeId: string;
    facilityId: number;
    facilityDocumentScanTypeVersionId: string;
    data: Record<string, unknown>;
}) {
    const result = await request({
        ...ROUTES.ADMIN_UPDATE_DOCUMENT_SCAN_TYPE_FACILITY_OVERRIDE_DRAFT,
        pathParams: { documentScanTypeId, facilityId, facilityDocumentScanTypeVersionId },
        data,
    });
    return result.data.draftVersion;
}

export async function publishDocumentScanTypeFacilityOverrideDraft({
    documentScanTypeId,
    facilityId,
    facilityDocumentScanTypeVersionId,
}: {
    documentScanTypeId: string;
    facilityId: number;
    facilityDocumentScanTypeVersionId: string;
}) {
    const result = await request({
        ...ROUTES.ADMIN_PUBLISH_DOCUMENT_SCAN_TYPE_FACILITY_OVERRIDE_DRAFT,
        pathParams: { documentScanTypeId, facilityId, facilityDocumentScanTypeVersionId },
    });
    return result.data;
}

export async function discardDocumentScanTypeFacilityOverrideDraft({
    documentScanTypeId,
    facilityId,
    facilityDocumentScanTypeVersionId,
}: {
    documentScanTypeId: string;
    facilityId: number;
    facilityDocumentScanTypeVersionId: string;
}) {
    const result = await request({
        ...ROUTES.ADMIN_DISCARD_DOCUMENT_SCAN_TYPE_FACILITY_OVERRIDE_DRAFT,
        pathParams: { documentScanTypeId, facilityId, facilityDocumentScanTypeVersionId },
    });
    return result.data;
}

export async function createFacilityDocumentScanTypeDraft({
    facilityId,
    facilityDocumentScanTypeId,
}: {
    facilityId: number;
    facilityDocumentScanTypeId: string;
}) {
    const result = await request({
        ...ROUTES.ADMIN_CREATE_FACILITY_DOCUMENT_SCAN_TYPE_DRAFT,
        pathParams: { facilityId, facilityDocumentScanTypeId },
    });
    return result.data.draftVersion;
}

export async function updateFacilityDocumentScanTypeDraft({
    facilityId,
    facilityDocumentScanTypeId,
    facilityDocumentScanTypeVersionId,
    data,
}: {
    facilityId: number;
    facilityDocumentScanTypeId: string;
    facilityDocumentScanTypeVersionId: string;
    data: Record<string, unknown>;
}) {
    const result = await request({
        ...ROUTES.ADMIN_UPDATE_FACILITY_DOCUMENT_SCAN_TYPE_DRAFT,
        pathParams: { facilityId, facilityDocumentScanTypeId, facilityDocumentScanTypeVersionId },
        data,
    });
    return result.data.draftVersion;
}

export async function publishFacilityDocumentScanTypeDraft({
    facilityId,
    facilityDocumentScanTypeId,
    facilityDocumentScanTypeVersionId,
}: {
    facilityId: number;
    facilityDocumentScanTypeId: string;
    facilityDocumentScanTypeVersionId: string;
}) {
    const result = await request({
        ...ROUTES.ADMIN_PUBLISH_FACILITY_DOCUMENT_SCAN_TYPE_DRAFT,
        pathParams: { facilityId, facilityDocumentScanTypeId, facilityDocumentScanTypeVersionId },
    });
    return result.data;
}
